/**
 * DataTable — the canonical wrapper + cell primitives for every list
 * view in AUTO-REMAP (admin Orders, customer Transactions, Tickets,
 * Customers, Price catalog…).
 *
 * Phase-1 of the design refresh: replaces the bright cyan→blue gradient
 * table headers with calm white headers + slate-200 hairline borders,
 * tabular-nums numeric cells right-aligned, hover row state, and an
 * optional density toggle so 4K admins can opt into a "Spacious" view
 * while laptop ops stay on "Compact".
 *
 * Usage:
 *   <DataTable density={density} onDensityChange={setDensity}
 *              testid="admin-orders-table">
 *     <DataTableHead>
 *       <DataTH>Order №</DataTH>
 *       <DataTH>Customer</DataTH>
 *       <DataTH numeric>Amount</DataTH>
 *       <DataTH>Status</DataTH>
 *     </DataTableHead>
 *     <DataTableBody>
 *       {rows.map(r => (
 *         <DataTR key={r.id} onClick={() => open(r)}>
 *           <DataTD primary>{r.number}</DataTD>
 *           <DataTD>{r.customer}</DataTD>
 *           <DataTD numeric>€ {r.amount}</DataTD>
 *           <DataTD><StatusBadge tone="success">Paid</StatusBadge></DataTD>
 *         </DataTR>
 *       ))}
 *     </DataTableBody>
 *   </DataTable>
 *
 * The component is purposefully unopinionated about WHERE the data
 * comes from — pages keep their fetch / filter / sort logic, this
 * just supplies the visual shell + utility primitives.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Rows3, Rows, Maximize2 } from 'lucide-react';

// ─────────────── Density state hook ──────────────────────────────
const STORAGE_KEY = 'ar.data-table.density';
const DENSITIES = ['compact', 'default', 'spacious'];

/** Per-table density persisted in localStorage. Each table passes its
 *  own `id` so admins can have e.g. Orders=Compact, Customers=Default. */
export function useTableDensity(id, fallback = 'default') {
  const key = `${STORAGE_KEY}.${id || 'global'}`;
  const [density, setDensity] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return DENSITIES.includes(stored) ? stored : fallback;
    } catch (_e) { return fallback; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, density); } catch (_e) { /* private mode */ }
  }, [key, density]);
  return [density, setDensity];
}

// ─────────────── Density toggle button group ─────────────────────
export function DensityToggle({ value, onChange, className = '' }) {
  const options = [
    { id: 'compact',  label: 'Compact',  Icon: Rows3 },
    { id: 'default',  label: 'Default',  Icon: Rows },
    { id: 'spacious', label: 'Spacious', Icon: Maximize2 },
  ];
  return (
    <div
      className={`inline-flex items-center bg-slate-100 rounded-lg p-0.5 ${className}`}
      role="radiogroup"
      aria-label="Row density"
    >
      {options.map(({ id, label, Icon }) => {
        const active = value === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            data-testid={`density-${id}`}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
              active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            aria-checked={active}
            role="radio"
            title={label}
          >
            <Icon className="w-3 h-3" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Row vertical padding per density level. Cells use `py-X` derived from
// these tokens so the whole table breathes together.
const ROW_PY = {
  compact:  'py-1.5',
  default:  'py-2.5',
  spacious: 'py-4',
};

// ─────────────── Wrapper ─────────────────────────────────────────
export function DataTable({ children, density = 'default', className = '', testid }) {
  return (
    <div
      className={`bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden ${className}`}
      data-testid={testid}
      data-density={density}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: 'fit-content' }}>
          {children}
        </table>
      </div>
    </div>
  );
}

// ─────────────── Head / Body wrappers ────────────────────────────
export function DataTableHead({ children, sticky = true }) {
  return (
    <thead className={`bg-white border-b border-slate-200 ${sticky ? 'sticky top-0 z-10' : ''}`}>
      <tr>{children}</tr>
    </thead>
  );
}

export function DataTableBody({ children }) {
  return <tbody className="divide-y divide-slate-100">{children}</tbody>;
}

// ─────────────── TH (header cell) ────────────────────────────────
export function DataTH({ children, numeric = false, className = '' }) {
  return (
    <th
      scope="col"
      className={`px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap ${
        numeric ? 'text-right' : 'text-left'
      } ${className}`}
    >
      {children}
    </th>
  );
}

// ─────────────── TR (body row) ───────────────────────────────────
export function DataTR({ children, onClick, selected = false, density = 'default', className = '' }) {
  const clickable = !!onClick;
  return (
    <tr
      onClick={onClick}
      className={`group transition-colors ${
        selected ? 'bg-cyan-50/60' : 'hover:bg-slate-50'
      } ${clickable ? 'cursor-pointer' : ''} ${className}`}
      data-density={density}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, { _density: density })
          : child,
      )}
    </tr>
  );
}

// ─────────────── TD (body cell) — visual variants via props ─────
export function DataTD({
  children,
  numeric = false,
  primary = false,     // Order №, ticket ID — accent-coloured + mono
  secondary = false,   // VIN, ECU — small + slate-500
  truncate = false,    // long service lists, descriptions
  maxWidth,            // px — caps a column without forcing wrap
  className = '',
  _density = 'default',
  ...rest
}) {
  const py = ROW_PY[_density] || ROW_PY.default;
  const base = `px-3 ${py} align-middle text-sm`;
  const numericCls   = numeric   ? 'text-right tabular-nums text-slate-900 font-medium' : 'text-slate-700';
  const primaryCls   = primary   ? 'font-mono-ui font-medium text-cyan-700 text-[13px] tabular-nums' : '';
  const secondaryCls = secondary ? 'text-[12px] text-slate-500' : '';
  const truncateCls  = truncate  ? 'truncate' : 'whitespace-nowrap';
  const style = maxWidth ? { maxWidth: `${maxWidth}px` } : undefined;
  return (
    <td
      className={`${base} ${primary ? primaryCls : numericCls} ${secondaryCls} ${truncateCls} ${className}`}
      style={style}
      {...rest}
    >
      {children}
    </td>
  );
}

// ─────────────── Empty state row ─────────────────────────────────
export function DataTableEmpty({ colSpan, icon, title, message, action }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-16 text-center">
        <div className="flex flex-col items-center gap-2 text-slate-500">
          {icon && <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">{icon}</div>}
          {title && <div className="text-sm font-medium text-slate-700">{title}</div>}
          {message && <div className="text-xs text-slate-500 max-w-sm">{message}</div>}
          {action && <div className="mt-2">{action}</div>}
        </div>
      </td>
    </tr>
  );
}
